/**
 * Bridge Coach - AI-powered suggestions and odds calculation for Contract Bridge
 */

// High Card Points (HCP) calculation
export const calculateHCP = (hand) => {
  if (!hand || hand.length === 0) return 0;
  
  let hcp = 0;
  hand.forEach(card => {
    const cardValue = card.value || card.rank; // Support both value and rank
    if (cardValue === 14) hcp += 4;      // Ace
    else if (cardValue === 13) hcp += 3; // King
    else if (cardValue === 12) hcp += 2; // Queen
    else if (cardValue === 11) hcp += 1; // Jack
  });
  
  return hcp;
};

// Calculate distribution points
export const calculateDistributionPoints = (hand) => {
  if (!hand || hand.length === 0) return { points: 0, shape: '' };
  
  const suits = { spades: 0, hearts: 0, diamonds: 0, clubs: 0 };
  hand.forEach(card => {
    suits[card.suit] = (suits[card.suit] || 0) + 1;
  });
  
  let distPoints = 0;
  const lengths = Object.values(suits).sort((a, b) => b - a);
  const shape = lengths.join('-');
  
  // Void = 3 points, Singleton = 2 points, Doubleton = 1 point
  Object.values(suits).forEach(count => {
    if (count === 0) distPoints += 3;
    else if (count === 1) distPoints += 2;
    else if (count === 2) distPoints += 1;
  });
  
  // Long suit points (5+ cards = 1 point each extra)
  Object.values(suits).forEach(count => {
    if (count >= 5) distPoints += count - 4;
  });
  
  return { points: distPoints, shape, lengths };
};

// Analyze suit holdings
export const analyzeSuits = (hand) => {
  const suits = {
    spades: { cards: [], hcp: 0, length: 0, honors: 0 },
    hearts: { cards: [], hcp: 0, length: 0, honors: 0 },
    diamonds: { cards: [], hcp: 0, length: 0, honors: 0 },
    clubs: { cards: [], hcp: 0, length: 0, honors: 0 }
  };
  
  // Map Bridge suit names to lowercase keys
  const suitMap = {
    'SPADES': 'spades',
    'HEARTS': 'hearts', 
    'DIAMONDS': 'diamonds',
    'CLUBS': 'clubs',
    'spades': 'spades',
    'hearts': 'hearts',
    'diamonds': 'diamonds',
    'clubs': 'clubs'
  };
  
  hand.forEach(card => {
    const suitKey = suitMap[card.suit];
    const suit = suits[suitKey];
    const cardValue = card.value || card.rank;
    
    if (suit) {
      suit.cards.push(card);
      suit.length++;
      if (cardValue >= 11) {
        suit.honors++;
        if (cardValue === 14) suit.hcp += 4;
        else if (cardValue === 13) suit.hcp += 3;
        else if (cardValue === 12) suit.hcp += 2;
        else if (cardValue === 11) suit.hcp += 1;
      }
    }
  });
  
  return suits;
};

// Get bidding suggestion
export const getBiddingSuggestion = (hand, biddingHistory, isDealer, vulnerability) => {
  const hcp = calculateHCP(hand);
  const { points: distPoints, shape } = calculateDistributionPoints(hand);
  const totalPoints = hcp + distPoints;
  const suits = analyzeSuits(hand);
  
  // Find longest/strongest suit
  let bestSuit = null;
  let bestLength = 0;
  ['spades', 'hearts', 'diamonds', 'clubs'].forEach(suit => {
    if (suits[suit].length > bestLength || 
        (suits[suit].length === bestLength && suits[suit].hcp > (bestSuit ? suits[bestSuit].hcp : 0))) {
      bestSuit = suit;
      bestLength = suits[suit].length;
    }
  });
  
  const hasMajor5 = suits.spades.length >= 5 || suits.hearts.length >= 5;
  const balanced = shape === '4-3-3-3' || shape === '4-4-3-2' || shape === '5-3-3-2';
  
  let suggestion = { bid: 'Pass', reason: '', confidence: 50 };
  
  // Opening bids
  if (biddingHistory.length === 0 || biddingHistory.every(b => b === 'Pass')) {
    if (hcp >= 22) {
      suggestion = { bid: '2♣', reason: 'Very strong hand (22+ HCP) - forcing bid', confidence: 95 };
    } else if (hcp >= 20 && balanced) {
      suggestion = { bid: '2NT', reason: 'Balanced 20-21 HCP - shows strength', confidence: 90 };
    } else if (hcp >= 15 && hcp <= 17 && balanced) {
      suggestion = { bid: '1NT', reason: 'Balanced 15-17 HCP - classic 1NT opening', confidence: 90 };
    } else if (totalPoints >= 13) {
      if (suits.spades.length >= 5) {
        suggestion = { bid: '1♠', reason: '5+ spades with opening values', confidence: 85 };
      } else if (suits.hearts.length >= 5) {
        suggestion = { bid: '1♥', reason: '5+ hearts with opening values', confidence: 85 };
      } else if (suits.diamonds.length >= 4) {
        suggestion = { bid: '1♦', reason: '4+ diamonds, preparing rebid', confidence: 80 };
      } else {
        suggestion = { bid: '1♣', reason: 'Opening with clubs (may be short)', confidence: 75 };
      }
    } else if (totalPoints >= 10 && totalPoints <= 12 && bestLength >= 6) {
      const suitSymbol = bestSuit === 'spades' ? '♠' : bestSuit === 'hearts' ? '♥' : bestSuit === 'diamonds' ? '♦' : '♣';
      suggestion = { bid: `2${suitSymbol}`, reason: `Weak two with 6+ ${bestSuit}`, confidence: 70 };
    } else {
      suggestion = { bid: 'Pass', reason: 'Not enough strength to open', confidence: 80 };
    }
  }
  // Responding to partner's bid
  else {
    const lastBid = biddingHistory[biddingHistory.length - 1];
    const partnerBid = biddingHistory.length >= 2 ? biddingHistory[biddingHistory.length - 2] : null;
    
    if (lastBid === '1NT' && partnerBid === null) {
      // Responding to 1NT
      if (hcp >= 10) {
        suggestion = { bid: '3NT', reason: 'Game values opposite 1NT', confidence: 85 };
      } else if (suits.spades.length >= 5 || suits.hearts.length >= 5) {
        const transferTo = suits.hearts.length >= 5 ? '2♦' : '2♥';
        suggestion = { bid: transferTo, reason: 'Transfer to your 5-card major', confidence: 80 };
      } else if (hcp >= 8) {
        suggestion = { bid: '2NT', reason: 'Invitational raise', confidence: 75 };
      }
    }
  }
  
  return { ...suggestion, hcp, distPoints, totalPoints, suits, shape };
};

// Get play suggestion during card play
export const getPlaySuggestion = (state) => {
  const { 
    playerHand, 
    currentTrick, 
    trumpSuit, 
    isLeading,
    contract,
    tricksWon,
    tricksNeeded,
    playedCards
  } = state;
  
  if (!playerHand || playerHand.length === 0) {
    return { card: null, reason: 'No cards to play', confidence: 0 };
  }
  
  const suits = analyzeSuits(playerHand);
  
  // Leading a trick
  if (isLeading || currentTrick.length === 0) {
    // Lead trump if we have length
    if (trumpSuit && suits[trumpSuit] && suits[trumpSuit].length >= 3) {
      const trumpCards = suits[trumpSuit].cards.sort((a, b) => b.rank - a.rank);
      return {
        card: trumpCards[0],
        reason: 'Lead high trump to draw opponents\' trumps',
        confidence: 75
      };
    }
    
    // Lead from longest suit
    let longestSuit = null;
    let maxLength = 0;
    Object.entries(suits).forEach(([suit, data]) => {
      if (data.length > maxLength) {
        maxLength = data.length;
        longestSuit = suit;
      }
    });
    
    if (longestSuit && suits[longestSuit].cards.length > 0) {
      const suitCards = suits[longestSuit].cards.sort((a, b) => b.rank - a.rank);
      // Lead 4th best from length
      const cardIndex = Math.min(3, suitCards.length - 1);
      return {
        card: suitCards[cardIndex],
        reason: `Lead 4th best from your longest suit (${longestSuit})`,
        confidence: 70
      };
    }
  }
  
  // Following to a trick
  if (currentTrick.length > 0) {
    const leadSuit = currentTrick[0].suit;
    const leadCard = currentTrick[0];
    const mySuitCards = suits[leadSuit]?.cards || [];
    
    // Must follow suit if possible
    if (mySuitCards.length > 0) {
      const sortedCards = mySuitCards.sort((a, b) => b.rank - a.rank);
      const winningCard = currentTrick.reduce((best, card) => 
        card.suit === leadSuit && card.rank > best.rank ? card : best, 
        { rank: 0 }
      );
      
      // Can we win the trick?
      const winnableCards = sortedCards.filter(c => c.rank > winningCard.rank);
      if (winnableCards.length > 0) {
        return {
          card: winnableCards[winnableCards.length - 1], // Win with lowest winner
          reason: 'Win the trick with your lowest winning card',
          confidence: 80
        };
      } else {
        // Can't win - play low
        return {
          card: sortedCards[sortedCards.length - 1],
          reason: 'Cannot win - discard your lowest card',
          confidence: 85
        };
      }
    }
    
    // Can't follow suit - consider trumping
    if (trumpSuit && suits[trumpSuit] && suits[trumpSuit].cards.length > 0) {
      const trumpCards = suits[trumpSuit].cards.sort((a, b) => a.rank - b.rank);
      return {
        card: trumpCards[0], // Trump with lowest
        reason: 'Trump the trick with your lowest trump',
        confidence: 75
      };
    }
    
    // No trump, no suit - discard
    let worstCard = null;
    let worstValue = Infinity;
    Object.values(suits).forEach(suitData => {
      suitData.cards.forEach(card => {
        const value = card.rank + (suitData.length > 3 ? 10 : 0); // Keep long suits
        if (value < worstValue) {
          worstValue = value;
          worstCard = card;
        }
      });
    });
    
    return {
      card: worstCard || playerHand[0],
      reason: 'Discard from your shortest/weakest suit',
      confidence: 70
    };
  }
  
  return { card: playerHand[0], reason: 'Play a card', confidence: 50 };
};

// Calculate odds of making contract
export const calculateContractOdds = (state) => {
  const { tricksWon, tricksNeeded, trumpSuit, playerHand, dummyHand, contract } = state;
  
  const tricksRemaining = 13 - (tricksWon.declarer + tricksWon.defense);
  const tricksStillNeeded = Math.max(0, tricksNeeded - tricksWon.declarer);
  
  if (tricksStillNeeded === 0) return 100; // Already made it
  if (tricksStillNeeded > tricksRemaining) return 0; // Impossible
  
  // Simplified odds based on tricks needed vs remaining
  const baseOdds = ((tricksRemaining - tricksStillNeeded + 1) / tricksRemaining) * 100;
  
  // Adjust for high cards
  const combinedHCP = calculateHCP(playerHand || []) + calculateHCP(dummyHand || []);
  const hcpBonus = (combinedHCP - 20) * 2;
  
  return Math.max(5, Math.min(95, baseOdds + hcpBonus));
};

// Learning mode explanations for Bridge
export const getBridgeLearningExplanation = (phase) => {
  const explanations = {
    bidding: {
      title: "Bidding Phase",
      content: "Communicate with partner through bids. Each bid shows points (HCP) and suit length. Goal: find the best contract."
    },
    playing: {
      title: "Card Play",
      content: "Declarer tries to make the contract. Defense tries to set it. Follow suit if possible. Highest card of led suit wins (unless trumped)."
    },
    dummy: {
      title: "The Dummy",
      content: "After the opening lead, dummy's hand is exposed. Declarer plays both hands. Plan your play before touching a card!"
    }
  };
  
  return explanations[phase] || explanations.bidding;
};

// Contextual tips for Bridge
export const getBridgeContextualTips = (phase, situation) => {
  const tips = {
    bidding: [
      "Open 1NT with 15-17 HCP and balanced shape (4-3-3-3, 4-4-3-2, 5-3-3-2).",
      "With 5+ cards in a major (♠ or ♥), bid that suit first.",
      "Support partner's major with 3+ cards - raising shows 6-9 points.",
      "Don't bid a new suit at the 2-level without 10+ points.",
      "Game contracts: 3NT, 4♥, 4♠ need ~25+ combined points."
    ],
    playing: [
      "Count your winners before playing from dummy.",
      "Draw trumps early unless you need to ruff in dummy.",
      "Lead toward honors (K, Q, J) to develop tricks.",
      "Second hand low, third hand high (general guideline).",
      "Watch the cards played to track what's still out."
    ],
    defense: [
      "Lead partner's suit if they bid one.",
      "Lead 4th best from your longest and strongest suit vs NT.",
      "Lead top of a sequence (KQJ, QJ10).",
      "Return partner's lead unless you have a clear alternative.",
      "Signal encouragement with a high card, discouragement with low."
    ]
  };
  
  return tips[phase] || tips.bidding;
};

export default {
  calculateHCP,
  calculateDistributionPoints,
  analyzeSuits,
  getBiddingSuggestion,
  getPlaySuggestion,
  calculateContractOdds,
  getBridgeLearningExplanation,
  getBridgeContextualTips
};
